import { BehaviorSubject, Observable } from 'rxjs';
import { filter, finalize, map, share, switchMap } from 'rxjs/operators';

import { filterDefined } from 'utils/is-defined';

interface QueueItem<T = any> {
  id?: any;
  invoker: Observable<T>;
}

/**
 * RXJS observables queue
 *
 * @TODO i.ablov remake on native rxjs pipes
 */
export class Queue<T = any> {
  /** Queue buffer */
  private readonly buffer: BehaviorSubject<QueueItem<T>[]> =
    new BehaviorSubject([]);

  /** Queue execution stream */
  readonly execution$ = new Observable<
    QueueItem<T> & {
      result: T;
    }
  >(observer => {
    const currentObservable: BehaviorSubject<QueueItem<T> | null> =
      new BehaviorSubject(null);

    const bufferSubscription = this.buffer.subscribe(buffer => {
      if (buffer.length) {
        const currentTreatedSubscription = currentObservable.value;
        if (
          !currentTreatedSubscription ||
          !buffer.includes(currentTreatedSubscription)
        ) {
          const first = buffer[0];
          currentObservable.next(first);
        }
      }
    });

    const currentSubscription = currentObservable
      .pipe(
        filterDefined(),
        switchMap(item =>
          item.invoker.pipe(
            finalize(() => {
              const filteredBuffer = this.buffer.value.filter(i => i !== item);
              this.buffer.next(filteredBuffer);
            }),
          ),
        ),
      )
      .subscribe({
        next: result => {
          observer.next({
            id: currentObservable.value!.id,
            invoker: currentObservable.value!.invoker,
            result,
          });
        },
        error: error => {
          observer.error(error);
        },
      });

    return () => {
      bufferSubscription.unsubscribe();
      currentSubscription.unsubscribe();
      currentObservable.complete();
    };
  }).pipe(share());

  /**
   * Add observable to queue after subscription
   *
   * @param observable - stream to add
   * @param id - optional id for filtering
   */
  add$(observable: Observable<T>, id?: number): Observable<T> {
    return new Observable(observer => {
      const executionSubscription = this.execution$
        .pipe(
          filter(execution => execution.invoker === observable),
          map(execution => execution.result),
        )
        .subscribe(observer);

      this.add(observable, id);

      const bufferSubscription = this.buffer.subscribe(buffer => {
        if (buffer.every(task => task.invoker !== observable)) {
          observer.complete();

          executionSubscription.unsubscribe();
          bufferSubscription.unsubscribe();
        }
      });

      return () => {
        executionSubscription.unsubscribe();
        bufferSubscription.unsubscribe();
      };
    });
  }

  /**
   * Add observable to queue and start execution imidiately, if there is some subscribers
   *
   * @param observable - stream to add
   * @param id - optional id for filtering
   */
  add(observable: Observable<T>, id?: number): void {
    this.buffer.value.push({
      invoker: observable,
      id,
    });
    this.buffer.next(this.buffer.value);
  }
}
